import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { flatten, find, filter, isEmpty, cloneDeep } from 'lodash';

import useEvent from '@src/hooks/useEvent';
import { isExpired, isBetween, formatToTimestamp } from '@src/utils/time';
import { DEFAULT_SLOT_TYPE, DEFAULT_TICKET_TYPE, DEFAULT_TICKET_NUMBER } from '@src/const/event';

import TicketSelection from './ticketSelection';
import PriceButton from './priceButton';
import FixedPriceButton from './fixedPriceButton';

const PurchaseWrapper = styled.div``;

const StyledTicketSelection = styled(TicketSelection)`
	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const StyledPriceButton = styled(PriceButton)`
	margin-top: 12px;

	@media ${props => props.theme.device.tablet} {
		margin-top: 16px;
	}

	@media ${props => props.theme.device.smTablet} {
		margin-top: 16px;
	}

	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const getTimeForCountdown = notYetOnSaleTickets => {
	const sortedTickets = cloneDeep(notYetOnSaleTickets).sort((a, b) => (
		formatToTimestamp(a.saleStartTime) - formatToTimestamp(b.saleStartTime)
	));

	return isEmpty(notYetOnSaleTickets) ? '' : sortedTickets[0].saleStartTime;
};

interface IPurchase {
	className?: string;
}

const Purchase = ({ className }: IPurchase): JSX.Element => {
	const { t } = useTranslation();
	const { sortedEventSlots, handleAddToCart } = useEvent();
	const [slotType, setSlotType] = useState(DEFAULT_SLOT_TYPE);
	const [ticketType, setTicketType] = useState(DEFAULT_TICKET_TYPE);
	const [ticketNumber, setTicketNumber] = useState(DEFAULT_TICKET_NUMBER);
	const [dropdownError, setDropdownError] = useState({
		slotType: false,
		ticketType: false,
		ticketNumber: false,
	});

	const allTicketInfo = flatten(sortedEventSlots.map(slot => slot.ticket_info));
	const notYetOnSaleTickets = filter(allTicketInfo, ticket => isExpired({ startTime: ticket.saleStartTime }));
	const currentSaleTickets = filter(
		allTicketInfo,
		ticket => isBetween({ startTime: ticket.saleStartTime, endTime: ticket.saleEndTime }),
	);
	const isSaleEnd = !find(allTicketInfo, ticket => isExpired({ startTime: ticket.saleEndTime }));
	// Whether any tickets available for purchase currently are sold out or not.
	const isSoldOut = !isEmpty(currentSaleTickets) && !find(currentSaleTickets, ticket => ticket.remain > 0);
	/**
	 * Three scenario
	 * 1. All ticket will be on sale in the future.
	 * 2. Ticket whose sale time is current is sold out but another can be purchased in the future.
	 * 3. There are no ticket can be purchased currently but there are other tickets can be purchased in the future.
	 */
	const isNotYetOnSale = !find(allTicketInfo, ticket => isExpired({ endTime: ticket.saleStartTime })) || (
		isSoldOut && !isEmpty(notYetOnSaleTickets)
	) || (isEmpty(currentSaleTickets) && !isEmpty(notYetOnSaleTickets));
	const timeForCountdown = getTimeForCountdown(notYetOnSaleTickets);
	const showSoldOut = isEmpty(notYetOnSaleTickets) && (isSaleEnd || isSoldOut);
	const enablePurchase = !(showSoldOut || isNotYetOnSale);
	const canPurchase = (slotType !== DEFAULT_SLOT_TYPE) &&
			(ticketType !== DEFAULT_TICKET_TYPE) && (ticketNumber !== DEFAULT_TICKET_NUMBER);

	const handleSelectSlot = type => {
		setSlotType(type);
		setTicketType(DEFAULT_TICKET_TYPE);
		setTicketNumber(DEFAULT_TICKET_NUMBER);
	};
	const handleSelectTicket = type => {
		setTicketType(type);
		setTicketNumber(DEFAULT_TICKET_NUMBER);
	};
	const handleSelectTicketNumber = type => { setTicketNumber(type); };
	const handleAddEventToCart = () => {
		if (canPurchase) {
			handleAddToCart({
				slotId: slotType,
				tickets: [{
					ticket_id: ticketType,
					quantity: ticketNumber,
				}],
			});
		} else {
			setDropdownError({
				slotType: slotType === DEFAULT_SLOT_TYPE,
				ticketType: ticketType === DEFAULT_TICKET_TYPE,
				ticketNumber: ticketNumber === DEFAULT_TICKET_NUMBER,
			});
		}
	};
	const resetDropdownError = () => {
		setDropdownError({
			slotType: false,
			ticketType: false,
			ticketNumber: false,
		});
	};

	useEffect(() => {
		setDropdownError(prev => ({ ...prev, slotType: false }));
	}, [slotType]);

	useEffect(() => {
		setDropdownError(prev => ({ ...prev, ticketType: false }));
	}, [ticketType]);

	useEffect(() => {
		setDropdownError(prev => ({ ...prev, ticketNumber: false }));
	}, [ticketNumber]);

	return (
		<PurchaseWrapper className={className}>
			<StyledTicketSelection
				showCountdown={isNotYetOnSale}
				timeForCountdown={timeForCountdown}
				showSoldOut={showSoldOut}
				enablePurchase={enablePurchase}
				slotType={slotType}
				ticketType={ticketType}
				ticketNumber={ticketNumber}
				dropdownError={dropdownError}
				handleSelectSlot={handleSelectSlot}
				handleSelectTicket={handleSelectTicket}
				handleSelectTicketNumber={handleSelectTicketNumber}
			/>
			<StyledPriceButton
				slotType={slotType}
				ticketType={ticketType}
				buttonText={showSoldOut ? t('event:sold_out_tag') : t('event:add_to_cart')}
				isDisable={!enablePurchase}
				handleButtonClick={handleAddEventToCart}
			/>
			<FixedPriceButton
				showCountdown={isNotYetOnSale}
				timeForCountdown={timeForCountdown}
				showSoldOut={showSoldOut}
				slotType={slotType}
				ticketType={ticketType}
				ticketNumber={ticketNumber}
				canPurchase={canPurchase}
				dropdownError={dropdownError}
				handleSelectSlot={handleSelectSlot}
				handleSelectTicket={handleSelectTicket}
				handleSelectTicketNumber={handleSelectTicketNumber}
				handleAddEventToCart={handleAddEventToCart}
				resetDropdownError={resetDropdownError}
			/>
		</PurchaseWrapper>
	);
};

export default Purchase;
